import { z } from "zod";
import { ulidSchema } from "./schemas.js";
export const qvcFulfillmentSchemaVersionSchema = z.literal("2026-09-16.1");
const qvcSha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
export const qvcFulfillmentStateSchema = z.enum([
    "QUEUED",
    "EVIDENCE_READY",
    "SUBMISSION_IN_PROGRESS",
    "RECONCILIATION_REQUIRED",
    "SUBMITTED",
    "IN_PRODUCTION",
    "SHIPPED",
    "DELIVERED",
    "NEEDS_ATTENTION",
    "CANCELLED",
    "PHYSICALLY_ACCEPTED",
    "PHYSICALLY_REJECTED",
]);
export const qvcFulfillmentModeSchema = z.enum([
    "AUTOMATED_SANDBOX",
    "MANUAL_SANDBOX",
    "MANUAL_PRODUCTION_SAMPLE",
]);
export const qvcArtifactManifestSchema = z
    .object({
    schemaVersion: qvcFulfillmentSchemaVersionSchema,
    bookId: ulidSchema,
    previewId: ulidSchema,
    sourceRevision: z.number().int().nonnegative(),
    profileId: z.string().min(1).max(200),
    specificationVersion: z.string().min(1).max(100),
    packageId: z.string().min(1).max(200),
    presentationFingerprint: qvcSha256Schema,
    artifacts: z
        .array(z
        .object({
        kind: z.enum(["INTERIOR", "COVER"]),
        checksumSha256: qvcSha256Schema,
        byteSize: z
            .number()
            .int()
            .positive()
            .max(500 * 1024 * 1024),
        pageCount: z.number().int().positive().max(1_000),
    })
        .strict())
        .length(2),
    manifestDigestSha256: qvcSha256Schema,
})
    .strict()
    .superRefine((manifest, context) => {
    if (new Set(manifest.artifacts.map((artifact) => artifact.kind)).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "The manifest must contain one interior and one cover.",
        });
});
export const qvcFulfillmentHandoffPurposeSchema = z.enum([
    "MANUAL_SUBMISSION",
    "AMBIGUITY_RECONCILIATION",
    "PHYSICAL_INSPECTION",
]);
export const qvcFulfillmentHandoffSchema = z
    .object({
    schemaVersion: qvcFulfillmentSchemaVersionSchema,
    handoffId: ulidSchema,
    purpose: qvcFulfillmentHandoffPurposeSchema,
    fulfillmentId: ulidSchema,
    caseVersion: z.number().int().nonnegative(),
    manifest: qvcArtifactManifestSchema,
    artifacts: z
        .array(z
        .object({
        kind: z.enum(["INTERIOR", "COVER"]),
        checksumSha256: qvcSha256Schema,
        byteSize: z
            .number()
            .int()
            .positive()
            .max(500 * 1024 * 1024),
        pageCount: z.number().int().positive().max(1_000),
        contentType: z.literal("application/pdf"),
        downloadUrl: z.string().url(),
        expiresIn: z.literal(300),
    })
        .strict())
        .length(2),
    issuedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
})
    .strict()
    .superRefine((handoff, context) => {
    if (new Set(handoff.artifacts.map((artifact) => artifact.kind)).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "The handoff must contain one interior and one cover.",
        });
    if (Date.parse(handoff.expiresAt) !== Date.parse(handoff.issuedAt) + 300_000)
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "The handoff must expire exactly five minutes after issue.",
        });
});
export const qvcInspectionCheckSchema = z.enum([
    "TRIM",
    "BLEED_AND_CROP",
    "PAGE_SEQUENCE",
    "CONTENT_MATCH",
    "COLOR",
    "TYPOGRAPHY",
    "COVER_AND_SPINE",
    "BINDING",
    "PACKAGE_CONDITION",
    "DELIVERY_DAMAGE",
    "VOUCHER_PACKAGE_USABILITY",
]);
export const qvcPhysicalInspectionSchema = z
    .object({
    schemaVersion: qvcFulfillmentSchemaVersionSchema,
    sampleKind: z.enum(["DIGITAL_FIXTURE", "PHYSICAL_SAMPLE"]),
    fixture: z.boolean(),
    conclusion: z.enum(["ACCEPTED", "REJECTED"]),
    inspectedAt: z.string().datetime(),
    reviewerId: z.string().trim().min(8).max(128),
    reviewerAttestationDigestSha256: qvcSha256Schema,
    manifestDigestSha256: qvcSha256Schema,
    pageComparisonDigestSha256: qvcSha256Schema,
    checks: z
        .array(z
        .object({
        check: qvcInspectionCheckSchema,
        passed: z.boolean(),
    })
        .strict())
        .length(qvcInspectionCheckSchema.options.length),
    photoEvidence: z
        .array(z
        .object({
        kind: z.enum(["COVER", "INTERIOR", "PACKAGE"]),
        digestSha256: qvcSha256Schema,
    })
        .strict())
        .min(3)
        .max(30),
})
    .strict()
    .superRefine((inspection, context) => {
    const checks = inspection.checks.map((entry) => entry.check);
    if (new Set(checks).size !== qvcInspectionCheckSchema.options.length)
        context.addIssue({
            code: "custom",
            path: ["checks"],
            message: "Every inspection check must appear exactly once.",
        });
    const photoKinds = new Set(inspection.photoEvidence.map((entry) => entry.kind));
    const requiredPhotoKinds = ["COVER", "INTERIOR", "PACKAGE"];
    if (requiredPhotoKinds.some((kind) => !photoKinds.has(kind)))
        context.addIssue({
            code: "custom",
            path: ["photoEvidence"],
            message: "Cover, interior, and package photo digests are required.",
        });
});
const expectedVersionSchema = z.number().int().nonnegative();
const operatorNoteCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
export const qvcFulfillmentActionSchema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("PREPARE_EVIDENCE"),
        expectedVersion: expectedVersionSchema,
        manifest: qvcArtifactManifestSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("START_SUBMISSION"),
        expectedVersion: expectedVersionSchema,
        mode: qvcFulfillmentModeSchema,
        leaseToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    })
        .strict(),
    z
        .object({
        action: z.literal("RECORD_SUBMITTED"),
        expectedVersion: expectedVersionSchema,
        leaseToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
        provider: z.enum(["LULU", "GELATO", "OTHER_REVIEWED"]),
        providerEnvironment: z.enum(["SANDBOX", "PRODUCTION"]),
        externalReference: z.string().trim().min(8).max(200),
        evidenceDigestSha256: qvcSha256Schema,
        submittedAt: z.string().datetime(),
    })
        .strict(),
    z
        .object({
        action: z.literal("RECORD_AMBIGUOUS"),
        expectedVersion: expectedVersionSchema,
        leaseToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
        provider: z.enum(["LULU", "GELATO", "OTHER_REVIEWED"]),
        providerEnvironment: z.enum(["SANDBOX", "PRODUCTION"]),
        lookupReferenceDigestSha256: qvcSha256Schema,
        occurredAt: z.string().datetime(),
    })
        .strict(),
    z
        .object({
        action: z.literal("RESOLVE_AMBIGUITY"),
        expectedVersion: expectedVersionSchema,
        resolution: z.enum(["FOUND", "NOT_FOUND"]),
        lookupEvidenceDigestSha256: qvcSha256Schema,
        externalReference: z.string().trim().min(8).max(200).optional(),
        resolvedAt: z.string().datetime(),
    })
        .strict()
        .superRefine((command, context) => {
        if (command.resolution === "FOUND" && !command.externalReference)
            context.addIssue({
                code: "custom",
                path: ["externalReference"],
                message: "A found submission requires its external reference.",
            });
        if (command.resolution === "NOT_FOUND" && command.externalReference)
            context.addIssue({
                code: "custom",
                path: ["externalReference"],
                message: "A not-found result cannot include an external reference.",
            });
    }),
    z
        .object({
        action: z.literal("RECORD_IN_PRODUCTION"),
        expectedVersion: expectedVersionSchema,
        occurredAt: z.string().datetime(),
        evidenceDigestSha256: qvcSha256Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("RECORD_SHIPMENT"),
        expectedVersion: expectedVersionSchema,
        carrier: z.string().trim().min(2).max(80),
        trackingReference: z.string().trim().min(6).max(200),
        shippedAt: z.string().datetime(),
        evidenceDigestSha256: qvcSha256Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("RECORD_DELIVERY"),
        expectedVersion: expectedVersionSchema,
        deliveredAt: z.string().datetime(),
        evidenceDigestSha256: qvcSha256Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("RECORD_INSPECTION"),
        expectedVersion: expectedVersionSchema,
        inspection: qvcPhysicalInspectionSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("MARK_NEEDS_ATTENTION"),
        expectedVersion: expectedVersionSchema,
        reasonCode: operatorNoteCodeSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("CANCEL"),
        expectedVersion: expectedVersionSchema,
        reasonCode: operatorNoteCodeSchema,
    })
        .strict(),
]);
export const qvcPilotMeasurementEventNameSchema = z.enum([
    "offer_viewed",
    "checkout_or_channel_order_confirmed",
    "voucher_allocated",
    "voucher_package_submitted",
    "voucher_shipped",
    "voucher_delivered",
    "activation_started",
    "email_verified",
    "voucher_redeemed",
    "first_source_started",
    "first_source_saved",
    "generation_started",
    "generation_completed",
    "book_approved",
    "print_submitted",
    "print_accepted",
    "shipment_recorded",
    "delivered",
    "completion_reminder_sent",
    "completion_reminder_suppressed",
    "delivery_follow_up_sent",
    "reorder_started",
    "reorder_paid",
]);
export const qvcLifecycleEventNameSchema = z.enum([
    ...qvcPilotMeasurementEventNameSchema.options,
    "reorder_accepted",
    "paused",
    "suppressed",
    "refunded",
    "deleted",
    "support_blocked",
    "operator_blocked",
]);
export const qvcLifecycleStateCodeSchema = z.enum([
    "READY",
    "COMPLETED",
    "NEEDS_ATTENTION",
    "PAUSED",
    "SUPPRESSED",
    "REFUNDED",
    "DELETED",
]);
export const qvcLifecycleCohortIdSchema = z.string().regex(/^[A-Z0-9][A-Z0-9_-]{2,39}$/u);
export const qvcLifecycleEventSchema = z
    .object({
    schemaVersion: qvcFulfillmentSchemaVersionSchema,
    eventId: ulidSchema,
    cohortId: qvcLifecycleCohortIdSchema,
    subjectId: ulidSchema,
    name: qvcLifecycleEventNameSchema,
    occurredAt: z.string().datetime(),
    state: qvcLifecycleStateCodeSchema,
    durationMs: z
        .number()
        .int()
        .nonnegative()
        .max(365 * 24 * 60 * 60 * 1_000)
        .optional(),
    quantityBand: z.enum(["ONE", "TWO_TO_FIVE", "SIX_TO_TWENTY"]).optional(),
    providerMode: z.enum(["NONE", "SANDBOX", "PRODUCTION", "MANUAL"]).optional(),
    operatorTouch: z.boolean(),
    test: z.boolean(),
    fixture: z.boolean(),
})
    .strict();
export const qvcLifecycleSummarySchema = z
    .object({
    totalEvents: z.number().int().nonnegative(),
    eligibleEvents: z.number().int().nonnegative(),
    fixtureOrTestEvents: z.number().int().nonnegative(),
    uniqueEligibleSubjects: z.number().int().nonnegative(),
    stages: z
        .object({
        offerViewed: z.number().int().nonnegative(),
        checkoutOrChannelOrderConfirmed: z.number().int().nonnegative(),
        voucherAllocated: z.number().int().nonnegative(),
        voucherPackageSubmitted: z.number().int().nonnegative(),
        voucherShipped: z.number().int().nonnegative(),
        voucherDelivered: z.number().int().nonnegative(),
        activationStarted: z.number().int().nonnegative(),
        emailVerified: z.number().int().nonnegative(),
        voucherRedeemed: z.number().int().nonnegative(),
        firstSourceStarted: z.number().int().nonnegative(),
        firstSourceSaved: z.number().int().nonnegative(),
        generationStarted: z.number().int().nonnegative(),
        generationCompleted: z.number().int().nonnegative(),
        bookApproved: z.number().int().nonnegative(),
        printSubmitted: z.number().int().nonnegative(),
        printAccepted: z.number().int().nonnegative(),
        shipmentRecorded: z.number().int().nonnegative(),
        delivered: z.number().int().nonnegative(),
        completionReminderSent: z.number().int().nonnegative(),
        completionReminderSuppressed: z.number().int().nonnegative(),
        deliveryFollowUpSent: z.number().int().nonnegative(),
        reorderStarted: z.number().int().nonnegative(),
        reorderPaid: z.number().int().nonnegative(),
        reorderAccepted: z.number().int().nonnegative(),
    })
        .strict(),
    conversionPercent: z
        .object({
        activationToEmailVerification: z.number().min(0).max(100).nullable(),
        emailVerificationToRedemption: z.number().min(0).max(100).nullable(),
        activationToGenerationStart: z.number().min(0).max(100).nullable(),
        generationStartToCompletion: z.number().min(0).max(100).nullable(),
        generationCompletionToApproval: z.number().min(0).max(100).nullable(),
        activationToApproval: z.number().min(0).max(100).nullable(),
        activationToFirstSourceStart: z.number().min(0).max(100).nullable(),
        firstSourceStartToSave: z.number().min(0).max(100).nullable(),
        approvalToPrintAcceptance: z.number().min(0).max(100).nullable(),
        printAcceptanceToDelivery: z.number().min(0).max(100).nullable(),
        deliveryToReorderStart: z.number().min(0).max(100).nullable(),
        reorderStartToPaid: z.number().min(0).max(100).nullable(),
        reorderPaidToAccepted: z.number().min(0).max(100).nullable(),
        deliveryToReorder: z.number().min(0).max(100).nullable(),
    })
        .strict(),
    operatorTouches: z.number().int().nonnegative(),
    exceptions: z.number().int().nonnegative(),
    suppressions: z.number().int().nonnegative(),
    medianDurationMs: z.record(z.string(), z.number().int().nonnegative()),
})
    .strict();
export const qvcLifecycleReportResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: qvcFulfillmentSchemaVersionSchema,
        cohortId: qvcLifecycleCohortIdSchema,
        sourceReadComplete: z.literal(true),
        sourcePageCount: z.number().int().nonnegative(),
        sourceEventCount: z.number().int().nonnegative().max(25_000),
        sourceDigestSha256: qvcSha256Schema,
        firstOccurredAt: z.string().datetime().nullable(),
        lastOccurredAt: z.string().datetime().nullable(),
        eligiblePilotEvidence: z.boolean(),
        eventCounts: z
            .array(z
            .object({
            name: qvcLifecycleEventNameSchema,
            eligible: z.number().int().nonnegative(),
            testOrFixture: z.number().int().nonnegative(),
        })
            .strict())
            .max(qvcLifecycleEventNameSchema.options.length),
        summary: qvcLifecycleSummarySchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=qvc-fulfillment.js.map